import React, { useState, useMemo } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Text, Searchbar } from 'react-native-paper';
import Icon from 'react-native-vector-icons/FontAwesome';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { useGetAmAssetsQuery } from '../redux/services/amassetApi';

import T from '../Components/T';
import { tp } from '../utils/translateText';
const AmAssetList = ({ navigation }: any) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const company = gdata?.company;
  const [searchQuery, setSearchQuery] = useState('');

  const { data: assets = [], isLoading } = useGetAmAssetsQuery(company || '', {
    skip: !company
  });

  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return assets;
    const q = searchQuery.toLowerCase();
    return assets.filter(a =>
      a.as_code.toLowerCase().includes(q) ||
      a.as_name.toLowerCase().includes(q) ||
      (a.as_serialno?.toLowerCase().includes(q) ?? false) ||
      (a.as_location?.toLowerCase().includes(q) ?? false) ||
      (a.loc_name?.toLowerCase().includes(q) ?? false)
    );
  }, [assets, searchQuery]);

  const renderAssetItem = ({ item }: any) => (
    <TouchableOpacity
      style={styles.itemContainer}
      onPress={() => navigation.navigate('AmAssetDetail', { assetId: item.as_id })}
    >
      <View style={styles.itemContent}>
        <Text style={styles.itemTitle}>{item.as_name}</Text>
        <Text style={styles.itemCode}><T>Code:</T> {item.as_code}</Text>
        {item.as_serialno && <Text style={styles.itemSerial}><T>SN:</T> {item.as_serialno}</Text>}
        {(item.loc_name || item.as_location) ? (
          <View style={styles.locRow}>
            <Icon name="map-marker" size={12} color="#666" style={styles.statusIcon} />
            <Text style={styles.itemSerial} numberOfLines={1}>{item.loc_name || item.as_location}</Text>
            {item.loc_access === 'M' || item.loc_access === 'S' ? (
              <View style={[styles.accessBadge, item.loc_access === 'M' ? styles.accessMgmt : styles.accessStaff]}>
                <Text style={styles.accessText}>{item.loc_access === 'M' ? 'Mgmt only' : 'Staff only'}</Text>
              </View>
            ) : null}
          </View>
        ) : null}
        <View style={styles.statusContainer}>
          <Icon
            name={item.as_status === 'Active' ? 'check-circle' : 'exclamation-circle'}
            size={14}
            color={item.as_status === 'Active' ? '#4CAF50' : '#FF9800'}
            style={styles.statusIcon}
          />
          <Text style={styles.statusText}>{item.as_status}</Text>
        </View>
      </View>
      <Icon name="chevron-right" size={20} color="#999" />
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder={tp("Search by code, name, or serial")}
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchbar}
      />

      {isLoading ? (
        <ActivityIndicator size="large" color="#2E7D32" style={styles.loader} />
      ) : filteredAssets.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Icon name="cube" size={50} color="#ccc" />
          <Text style={styles.emptyText}>
            {searchQuery ? 'No assets found' : 'No assets available'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={filteredAssets}
          renderItem={renderAssetItem}
          keyExtractor={item => String(item.as_id)}
          contentContainerStyle={styles.listContent}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5'
  },
  searchbar: {
    margin: 10
  },
  loader: {
    flex: 1,
    justifyContent: 'center'
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  emptyText: {
    marginTop: 10,
    fontSize: 16,
    color: '#999'
  },
  listContent: {
    padding: 10
  },
  itemContainer: {
    flexDirection: 'row',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    alignItems: 'center',
    borderLeftWidth: 4,
    borderLeftColor: '#2E7D32'
  },
  itemContent: {
    flex: 1,
    marginRight: 10
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4
  },
  itemCode: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2
  },
  itemSerial: {
    fontSize: 12,
    color: '#666',
    marginBottom: 6
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  statusIcon: {
    marginRight: 4
  },
  statusText: {
    fontSize: 11,
    color: '#666'
  },
  locRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  accessBadge: {
    marginLeft: 6,
    marginBottom: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 8,
    borderWidth: 1
  },
  accessMgmt: {
    borderColor: '#C62828'
  },
  accessStaff: {
    borderColor: '#EF6C00'
  },
  accessText: {
    fontSize: 10,
    color: '#444'
  }
});

export default AmAssetList;
